import React, { useEffect, useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { coy } from 'react-syntax-highlighter/dist/esm/styles/prism';

import Input from '../UI/Input';
import InputTextarea from '../UI/InputTextarea';
import styled from 'styled-components';
import { colors } from '@/constants/colors';
import { TableStore } from '@/pages/Admin/Stores';
import IconsLib from '@/utils/IconsLib';
import Btn from '../UI/Btn';
import { updateStore, uploadImageSlider } from '@/api/store';
import { useUIStore } from '@/store/useUIStore';
import { toast } from 'react-toastify';
import PortalGenerator from './PortalGenerator';

interface Props {
   data: TableStore;
}

interface TableStoreValues {
   _id: string;
   storename: string;
   description: string;
   location: string;
   logo: string;
   storeDomain: string;
   settings: {
      btnColor: string;
      btnLabel: string;
      btnEmbed: string;
      designTheme: string;
      colorTheme: string;
      imgs: string[];
      portalIp: string;
   };
}

// Apex domains (mysmartfi.ph, cloudinary.com, xendit.co, gcash.com,
// smartfi.ph) deliberately omitted — they were bypass vectors. PH users
// run HTTP Injector / SVPN-style apps that tunnel through any whitelisted
// IP; apex domains on shared CDNs are the worst offenders because their
// IPs serve millions of other tenants. Only specific FQDNs the buy flow
// actually needs are listed below. The operator's store subdomain
// (e.g. aizenkurtitsolutions.mysmartfi.ph) is auto-added separately
// from data.storeDomain — see useState init below.
const DEFAULT_WALLGARDEN = [
   // Store images, portal backgrounds / sliders / sounds. Replaced
   // res.cloudinary.com when that account was disabled and media moved to
   // Cloudflare R2 — see RETIRED_HOSTS, which drops the dead host.
   'smartficdn.site',
   // Xendit checkout
   'checkout.xendit.co',
   'checkout-ui-gateway.xendit.co',
   'ewallet-service-live.xendit.co',
   // GCash payment + JS/asset CDNs (offlinepkg + S3 surfaced via
   // PCAPdroid 2026-05-22 on AIZENKURT's phone — GCash app loads its
   // mini-program bundle from these before showing the PIN page).
   'payments.gcash.com',
   'offlinepkg.gcash.com',
   'm-gcash-com.s3.ap-southeast-1.amazonaws.com',
   // TODO(narrow): storage.googleapis.com is APEX. Every GCS bucket
   // worldwide lives at this host, so this re-opens an apex bypass vector
   // (smaller than cloudinary.com — GCS is mostly static, not a CDN proxy —
   // but skilled HTTP Injector users CAN abuse it). Verified 2026-05-22
   // that GCash's PIN flow loads required JS from here. Narrow this to
   // {bucket}.storage.googleapis.com once a PCAPdroid capture surfaces
   // the specific bucket name (e.g. m-gcash-bundles.storage.googleapis.com).
   'storage.googleapis.com',
   // GCash/Alipay PIN-auth backend. GCash is built on Ant Group's Alipay
   // infrastructure; the PIN flow (1) loads risk-SDK JS from Alipay CDN,
   // (2) computes a fraud signature, (3) POSTs to an Alipay gateway. If
   // any of these are blocked at the captive firewall, the user lands
   // on the PIN page but submission silently hangs (the "stuck at PIN"
   // symptom AIZENKURT's customers hit 2026-05-22).
   'irisk-sea.alipay.com',
   'gw.alipayobjects.com',
   'gw.alipay.com',
   'mobilegw.alipay.com',
   'mclient.alipay.com',
   'mas.alipay.com',
   'global.alipay.com',
   'render.alipay.com',
   'i.alipayobjects.com',
   'mdn.alipayobjects.com',
   'at.alicdn.com',
   // zamcs.com is a Mynt-branded front for an Alipay risk service:
   // CNAME resolves to sg.rr2.internet-intl.spanner.alipaydns.com
   // (same Alipay backend as irisk-sea.alipay.com). Used in PIN flow.
   'gw.zamcs.com',
   // Mynt (GCash infra)
   'mss.paas.mynt.xyz',
   'api.mynt.xyz',
   'login.mynt.xyz',
   'customer-segment-api.mynt.xyz',
   'mdap.paas.mynt.xyz',
   'mgs-gw.paas.mynt.xyz',
   // Maya
   'payments.maya.ph',
   'assets.maya.ph',
   'assets.paymaya.com',
   'api.mayabank.ph',
   'api.paymaya.com',
   'api-bnpl.mayabank.ph',
   // Google connectivity check
   // 'beacons.gcp.gvt2.com',
   // SmartFi API (stable VPS IP — could be tightened to dst-address=187.77.137.232)
   'api.smartfi.ph',
];

// Hostnames that USED TO BE in DEFAULT_WALLGARDEN but were removed
// (apex bypass vectors + retired Mynt endpoints). The generated MikroTik
// script emits `remove [find comment="..."]` lines for each of these
// at the top, so when an operator re-pastes a fresh script their router
// automatically drops the old entries instead of accumulating them.
const RETIRED_HOSTS = [
   'mysmartfi.ph',
   'cloudinary.com',
   // Media moved to R2 (smartficdn.site); nothing is served from this host
   // any more, so routers should drop it on the next re-paste.
   'res.cloudinary.com',
   'xendit.co',
   'gcash.com',
   'smartfi.ph',
   'gcash-api.pulseid.com',
];

// Build the MikroTik terminal script. Two-phase:
//   1. Remove retired hosts + every current host. Removing current
//      hosts before re-adding them forces RouterOS to re-resolve
//      dst-host= entries against today's DNS, fixing the stale-IP
//      cache trap that bit AIZENKURT 2026-05-22 (api.mynt.xyz cached
//      to an old Akamai IP weeks after the original paste).
//   2. Add every current host fresh.
// `remove [find comment="X"]` on a non-existent entry is a no-op,
// so this is safe to re-run.
const buildMikrotikScript = (hosts: string[]): string => {
   const unique = [...new Set(hosts)];
   const toRemove = [...new Set([...RETIRED_HOSTS, ...unique])];
   const removeLines = toRemove.map(
      (h) =>
         `/ip hotspot walled-garden ip remove [find comment="${h}"]`
   );
   const addLines = unique.map(
      (h) => `add action=accept dst-host=${h} comment="${h}"`
   );
   return [
      '# 1) Cleanup: drop retired apex hosts from prior pastes +',
      '#    force re-resolve of cached entries (workaround for the',
      '#    RouterOS dst-host= IP-cache going stale after CDN moves).',
      ...removeLines,
      '',
      '# 2) Fresh adds.',
      '/ip hotspot walled-garden ip',
      ...addLines,
   ].join('\n');
};

const DEFAULT_STORE_VALUES = {
   _id: '',
   storename: '',
   location: '',
   storeDomain: '',
   settings: {
      btnColor: '#B3D946',
      btnLabel: 'Pay via Gcash',
      btnEmbed: '',
      designTheme: 'modern' as const,
      colorTheme: '#B3D946',
      imgs: [] as string[],
   },
};

const UpdateStore = ({ data }: Props) => {
   // console.log(`----`, data);
   const [isLoading, setIsLoading] = useState(false);
   const [isUploadingLogo, setIsUploadingLogo] = useState(false);
   const { handleClose, handleRefresh } = useUIStore();
   const [tab, setTab] = useState<'client' | 'server' | 'portal'>('client');
   const [wallgarden, setWallgarden] = useState(
      [
         data.storeDomain.replace(/^https?:\/\//, '') || '444',
         ...DEFAULT_WALLGARDEN,
      ].filter(Boolean)
   );

   // console.log(`wallgarden`, wallgarden);

   const [values, setValues] = useState<TableStoreValues>({
      _id: '',
      storename: '',
      description: '',
      location: '',
      logo: '',
      storeDomain: '',
      settings: {
         btnColor: '#B3D946',
         btnLabel: 'Pay via Gcash',
         btnEmbed: '',
         designTheme: 'modern',
         colorTheme: '#B3D946',
         imgs: [],
         portalIp: '10.0.0.1',
      },
   });

   useEffect(() => {
      setValues({
         ...values,
         _id: data._id,
         storename: data.storename,
         description: data.description || '',
         location: data.location,
         logo: data.logo || '',
         storeDomain: data.storeDomain,
         settings: {
            btnColor: data.settings.btnColor,
            btnLabel: data.settings.btnLabel,
            btnEmbed: data.settings.btnEmbed,
            designTheme: data.settings.designTheme || 'modern',
            colorTheme: data.settings.colorTheme || '#B3D946',
            imgs: data.settings.imgs || [],
            portalIp: data.settings.portalIp || '10.0.0.1',
         },
      });
   }, [data]);

   const {
      storename,
      location,
      storeDomain,
      settings: { btnColor, btnLabel, btnEmbed, portalIp },
   } = values;
   // console.log(`---- values ----`, values);

   const BTN_TEMPLATES = [
      {
         key: 'gcash-blue',
         name: 'GCash Blue',
         gradient: 'linear-gradient(135deg, #0077eb 0%, #00a3ff 100%)',
         icon: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>`,
      },
      {
         key: 'lime-pulse',
         name: 'Lime Pulse',
         gradient: 'linear-gradient(135deg, #B3D946 0%, #7de8ff 100%)',
         textColor: '#0b1030',
         icon: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0b1030" stroke-width="2" stroke-linecap="round"><path d="M5 12.55a11 11 0 0 1 14 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><circle cx="12" cy="20" r="1" fill="#0b1030"/></svg>`,
      },
      {
         key: 'sunset',
         name: 'Sunset',
         gradient: 'linear-gradient(135deg, #ff6b35 0%, #ffb325 100%)',
         icon: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>`,
      },
      {
         key: 'royal-purple',
         name: 'Royal Purple',
         gradient: 'linear-gradient(135deg, #6a4dff 0%, #a855f7 100%)',
         icon: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>`,
      },
      {
         key: 'dark-pro',
         name: 'Dark Pro',
         gradient: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%)',
         icon: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>`,
      },
      {
         key: 'teal-wave',
         name: 'Teal Wave',
         gradient: 'linear-gradient(135deg, #20c997 0%, #0dcaf0 100%)',
         icon: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>`,
      },
   ];

   const getTemplate = (key: string) =>
      BTN_TEMPLATES.find((t) => t.key === key) || BTN_TEMPLATES[0];

   const buildEmbed = (domain: string, templateKey: string, label: string) => {
      const t = getTemplate(templateKey);
      const txtColor = (t as any).textColor || 'white';
      const iconStroke = txtColor === 'white' ? 'white' : txtColor;
      const icon = (t as any).textColor
         ? t.icon
         : t.icon.replace(/stroke="[^"]*"/g, `stroke="${iconStroke}"`);
      return `<a href="${domain}" target="_blank" rel="noopener" onclick="window.location.href='${domain}';return false;" style="background: ${t.gradient}; outline: none; border: none; width: 100%; height: 44px; border-radius: 8px; color: ${txtColor}; font-size: 14px; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; box-shadow: 0 4px 14px rgba(0,0,0,0.15); letter-spacing: 0.02em; text-decoration: none;">${icon} ${label}</a>`;
   };

   const handleBtnStyle = (templateKey: string) => {
      setValues((prev) => ({
         ...prev,
         settings: {
            ...prev.settings,
            btnColor: templateKey,
            btnEmbed: buildEmbed(storeDomain, templateKey, btnLabel),
         },
      }));
   };

   const handleCopyToClipboard = (value: string) => {
      navigator.clipboard
         .writeText(value || '')
         .then(() => {
            console.log(`Copied to clipboard`);
         })
         .catch((err) => console.error('Error copying text: ', err));
   };

   const handleTab = (t: 'client' | 'server' | 'portal') => setTab(t);

   const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      // setting update state
      if (name === 'btnLabel' || name === 'btnColor' || name === 'btnEmbed' || name === 'portalIp') {
         setValues((prev) => ({
            ...prev,
            settings: {
               ...prev.settings,
               [name]: value,
            },
         }));
      }

      setValues((prev) => {
         const newValues = { ...prev, [name]: value };
         // Update btnEmbed whenever btnLabel or storeDomain changes
         if (name === 'btnLabel' || name === 'storeDomain') {
            newValues.settings.btnEmbed = buildEmbed(
               name === 'storeDomain' ? value : storeDomain,
               btnColor || 'gcash-blue',
               name === 'btnLabel' ? value : btnLabel
            );
         }
         return newValues;
      });
   };

   const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) {
         toast.error('Logo must be under 5MB');
         return;
      }
      setIsUploadingLogo(true);
      try {
         const response = await uploadImageSlider(file);
         const imageUrl = response.data?.imageUrl || response.data?.data?.imageUrl || '';
         if (!imageUrl) {
            toast.error('Upload succeeded but no URL returned');
            return;
         }
         setValues((prev) => ({ ...prev, logo: imageUrl }));
         toast.success('Logo uploaded — click Update to save');
      } catch (err: any) {
         console.error('Logo upload failed:', err);
         toast.error(err?.response?.data?.error || 'Failed to upload logo');
      } finally {
         setIsUploadingLogo(false);
      }
   };

   const validateForm = () => {
      const newErrors = {
         storename: !storename.trim(),
         location: !location.trim(),
         btnLabel: !btnLabel.trim(),
         storeDomain: !storeDomain.trim(),
      };
      // setErrors(newErrors);
      return !Object.values(newErrors).some((error) => error);
   };

   const handleUpdateStore = async () => {
      if (isLoading) return;

      if (!validateForm()) {
         // toast.error('Please fill in all fields');
         return;
      }
      // if(!storename || !location || !btnLabel || !storeDomain) return;

      setIsLoading(true);
      updateStore({
         storeId: data._id,
         storename,
         description: values.description,
         location,
         logo: values.logo,
         settings: {
            btnColor,
            btnLabel,
            btnEmbed,
            designTheme: data.settings.designTheme || 'modern',
            colorTheme: data.settings.colorTheme || '#B3D946',
            imgs: data.settings.imgs || [],
            portalIp: portalIp || '10.0.0.1',
         },
      })
         .then(async (res) => {
            // console.log(`----`, res);
            // await new Promise(resolve => setTimeout(resolve, 1000));
            toast.success(res.data.msg, {
               position: 'top-right',
               autoClose: 2500,
               hideProgressBar: false,
               closeOnClick: true,
               pauseOnHover: true,
               draggable: true,
            });
            setIsLoading(false);

            handleClose();

            // Wait longer before triggering refresh to ensure server has processed the creation
            await new Promise((resolve) => setTimeout(resolve, 300));

            handleRefresh();
         })
         .catch((err) => {
            console.log(`Error`, err);
            toast.error((err as Error)?.message || 'Server Error', {
               position: 'top-right',
               autoClose: 2500,
               hideProgressBar: false,
               closeOnClick: true,
               pauseOnHover: true,
               draggable: true,
            });
         })
         .finally(() => {
            setIsLoading(false);
         });
   };

   // console.log(`---- values ----`, values);

   // Initialize values
   useEffect(() => {
      // setValues(DEFAULT_STORE_VALUES);
      // setWallgarden(DEFAULT_WALLGARDEN);
   }, []);

   // Cleanup when component unmounts
   useEffect(() => {
      return () => {
         // setValues(DEFAULT_STORE_VALUES);
         // setWallgarden(DEFAULT_WALLGARDEN);
         // console.log(`cleanup....`);
      };
   }, []);

   return (
      <Wrapper>
         <TopHeader>
            <TopMenu>
               <MenuItem
                  $active={tab === 'client' ? 'true' : 'false'}
                  onClick={() => handleTab('client')}
               >
                  Client
               </MenuItem>
               <MenuItem
                  $active={tab === 'server' ? 'true' : 'false'}
                  onClick={() => handleTab('server')}
               >
                  Server
               </MenuItem>
               <MenuItem
                  $active={tab === 'portal' ? 'true' : 'false'}
                  onClick={() => handleTab('portal')}
               >
                  Portal
               </MenuItem>
            </TopMenu>
         </TopHeader>

         {tab === 'portal' ? (
            <PortalGenerator
               storeName={storename}
               shopUrl={storeDomain}
               logo={values.logo}
               btnLabel={btnLabel || 'Pay via Gcash'}
               btnGradient={getTemplate(btnColor || 'gcash-blue').gradient}
               btnTextColor={(getTemplate(btnColor || 'gcash-blue') as any).textColor || 'white'}
            />
         ) : tab === 'client' ? (
            <ClientWrapper>
               <LogoSection>
                  <LogoPreview>
                     {values.logo ? (
                        <LogoImg src={values.logo} alt="Store logo" />
                     ) : (
                        <LogoPlaceholder>
                           <IconsLib icon="store-3-line" size="24px" color={colors.GRAY8} />
                        </LogoPlaceholder>
                     )}
                  </LogoPreview>
                  <LogoActions>
                     <LogoLabel>Store Logo</LogoLabel>
                     <LogoUploadBtn
                        onClick={() =>
                           (document.getElementById('logo-upload') as HTMLInputElement)?.click()
                        }
                     >
                        {isUploadingLogo ? 'Uploading...' : values.logo ? 'Change Logo' : 'Upload Logo'}
                     </LogoUploadBtn>
                     <input
                        id="logo-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        style={{ display: 'none' }}
                     />
                     {values.logo && (
                        <LogoRemoveBtn onClick={() => setValues((prev) => ({ ...prev, logo: '' }))}>
                           Remove
                        </LogoRemoveBtn>
                     )}
                  </LogoActions>
               </LogoSection>

               <Form>
                  <Input
                     label="Store Name"
                     name="storename"
                     value={storename}
                     placeholder="Enter your store name"
                     onChange={handleChange}
                  />
                  <Input
                     label="Location"
                     name="location"
                     value={location}
                     placeholder="Enter Vendo Location"
                     onChange={handleChange}
                  />
               </Form>
               <DescriptionSection>
                  <InputTextarea
                     label="Store Description"
                     name="description"
                     value={values.description}
                     placeholder="Describe your store, services, or WiFi offerings"
                     onChange={(e) => setValues((prev) => ({ ...prev, description: e.target.value }))}
                  />
               </DescriptionSection>
               <Form>
                  <Input
                     label="Portal Redirect IP"
                     name="portalIp"
                     value={portalIp || '10.0.0.1'}
                     placeholder="e.g. 10.0.0.1"
                     onChange={handleChange}
                  />
                  <Input
                     label="Button Label"
                     name="btnLabel"
                     value={btnLabel || 'Pay via Gcash'}
                     placeholder="Enter your button label"
                     onChange={handleChange}
                  />
               </Form>

               <TemplateSection>
                  <BtnLabel>Button Style</BtnLabel>
                  <TemplateGrid>
                     {BTN_TEMPLATES.map((t) => {
                        const active = btnColor === t.key;
                        const txtColor = (t as any).textColor || 'white';
                        return (
                           <TemplateCard
                              key={t.key}
                              $active={active}
                              onClick={() => handleBtnStyle(t.key)}
                           >
                              <TemplatePreview $gradient={t.gradient}>
                                 <span
                                    dangerouslySetInnerHTML={{ __html: t.icon }}
                                    style={{ display: 'flex', alignItems: 'center' }}
                                 />
                                 <TemplatePreviewLabel $color={txtColor}>
                                    {btnLabel || 'Pay via Gcash'}
                                 </TemplatePreviewLabel>
                              </TemplatePreview>
                              <TemplateName>{t.name}</TemplateName>
                           </TemplateCard>
                        );
                     })}
                  </TemplateGrid>
               </TemplateSection>

               <IntegrationContent>
                  <BtnLabel>Button Integration (HTML)</BtnLabel>

                  <CodeEditorWrapper>
                     <CodeEditorHead>
                        <div></div>
                        <CodeEditorHeadLabel
                           onClick={() => handleCopyToClipboard(btnEmbed || '')}
                        >
                           <span>Copy HTML</span>
                           <IconsLib
                              icon="file-copy-line"
                              size="15px"
                              color="white"
                           />
                        </CodeEditorHeadLabel>
                     </CodeEditorHead>
                     <CodeEditorBody>
                        <SyntaxHighlighter language="html" style={coy}>
                           {btnEmbed || ''}
                        </SyntaxHighlighter>
                     </CodeEditorBody>
                  </CodeEditorWrapper>
               </IntegrationContent>
               <Actions>
                  <ActionWrapper>
                     <div onClick={handleClose}>
                        <Btn
                           title="Close"
                           type="outline"
                           bgcolor={colors.GRAY7}
                           textcolor={colors.GRAY1}
                           size="small"
                        />
                     </div>
                     <div onClick={handleUpdateStore}>
                        <Btn
                           title={isLoading ? 'please wait...' : 'Update'}
                           type={isLoading ? 'disabled' : 'normal'}
                           bgcolor={isLoading ? colors.GRAY10 : colors.PRIMARY}
                           textcolor={isLoading ? colors.GRAY1 : 'white'}
                           size="small"
                           hasIcon={isLoading}
                           icon="loader"
                           iconPosition="right"
                        />
                     </div>
                  </ActionWrapper>
               </Actions>
            </ClientWrapper>
         ) : (
            <ServerWrapper>
               <IntegrationContent>
                  <CodeEditorWrapper>
                     <CodeEditorHead>
                        <CodeEditorHeadTitle>
                           [FOR NON-MIKROTIK SYSTEMS] - AdoPisoft / LPB / FoJR /
                           EasyWifi / WiFi5 / Wifi ng Bayan
                        </CodeEditorHeadTitle>
                        <CodeEditorHeadLabel
                           onClick={() =>
                              handleCopyToClipboard(wallgarden.join('\n'))
                           }
                        >
                           <span>Copy URL&nbsp;s</span>
                           <IconsLib
                              icon="file-copy-line"
                              size="15px"
                              color="white"
                           />
                        </CodeEditorHeadLabel>
                     </CodeEditorHead>
                     <CodeEditorBody>
                        <SyntaxHighlighter language="html" style={coy}>
                           {wallgarden.join('\n')}
                        </SyntaxHighlighter>
                     </CodeEditorBody>
                  </CodeEditorWrapper>
               </IntegrationContent>
               <IntegrationContent>
                  <CodeEditorWrapper>
                     <CodeEditorHead>
                        <CodeEditorHeadTitle>
                           [FOR TP-LINK OMADA (CLOUD/HARDWARE) SYSTEMS]
                        </CodeEditorHeadTitle>
                        <CodeEditorHeadLabel
                           onClick={() =>
                              handleCopyToClipboard(wallgarden.join('\n'))
                           }
                        >
                           <span>Copy URL&nbsp;s</span>
                           <IconsLib
                              icon="file-copy-line"
                              size="15px"
                              color="white"
                           />
                        </CodeEditorHeadLabel>
                     </CodeEditorHead>
                     <CodeEditorBody>
                        <SyntaxHighlighter language="html" style={coy}>
                           {wallgarden.join('\n')}
                        </SyntaxHighlighter>
                     </CodeEditorBody>
                  </CodeEditorWrapper>
               </IntegrationContent>

               <IntegrationContent>
                  <CodeEditorWrapper>
                     <CodeEditorHead>
                        <CodeEditorHeadTitle>
                           [FOR MIKROTIK - COPY AND RUN THIS SCRIPT ON THE
                           TERMINAL]
                        </CodeEditorHeadTitle>
                        <CodeEditorHeadLabel
                           onClick={() =>
                              handleCopyToClipboard(
                                 buildMikrotikScript(wallgarden)
                              )
                           }
                        >
                           <span>Copy cmd</span>
                           <IconsLib
                              icon="file-copy-line"
                              size="15px"
                              color="white"
                           />
                        </CodeEditorHeadLabel>
                     </CodeEditorHead>
                     <CodeEditorBody>
                        <SyntaxHighlighter language="html" style={coy}>
                           {buildMikrotikScript(wallgarden)}
                        </SyntaxHighlighter>
                     </CodeEditorBody>
                  </CodeEditorWrapper>
               </IntegrationContent>
            </ServerWrapper>
         )}
      </Wrapper>
   );
};

const Wrapper = styled.div`
   /* width: 700px; */

   @media (max-width: 768px) {
      width: 100%;
   }
`;

const TopHeader = styled.div`
   display: flex;
   justify-content: center;
   align-items: center;
   margin-bottom: 6px;
`;
const TopMenu = styled.div`
   display: inline-flex; /* Behaves like an inline element */
   justify-content: flex-start;
   margin-top: 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 4px;
   background: ${colors.GRAY14};
   width: auto;
   :first-child {
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
   }
   :last-child {
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
   }
`;

const MenuItem = styled.div<{
   $active: string;
}>`
   /* padding: 5px 10px; */
   /* border: 1px solid ${colors.GRAY14};
   border-radius: 4px; */
   padding: 3px 10px;
   cursor: pointer;
   transition: border-color 0.3s ease-in-out;
   font-size: 15px;
   background: ${({ $active }) =>
      $active === 'true' ? colors.GRAY10 : colors.GRAY14};
   user-select: none;
`;

const ClientWrapper = styled.div``;
const ServerWrapper = styled.div``;

const LogoSection = styled.div`
   display: flex;
   align-items: center;
   gap: 14px;
   margin-bottom: 12px;
   padding: 12px;
   border: 1px solid ${colors.GRAY13};
   border-radius: 8px;
`;

const LogoPreview = styled.div`
   width: 56px;
   height: 56px;
   border-radius: 10px;
   overflow: hidden;
   flex-shrink: 0;
   border: 1px solid ${colors.GRAY12};
`;

const LogoImg = styled.img`
   width: 100%;
   height: 100%;
   object-fit: cover;
`;

const LogoPlaceholder = styled.div`
   width: 100%;
   height: 100%;
   display: flex;
   align-items: center;
   justify-content: center;
   background: ${colors.GRAY14};
`;

const LogoActions = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
`;

const LogoLabel = styled.p`
   font-size: 13px;
   font-weight: 600;
   color: ${colors.GRAY1};
`;

const LogoUploadBtn = styled.button`
   background: none;
   border: none;
   color: ${colors.PRIMARY};
   font-size: 12px;
   font-weight: 500;
   cursor: pointer;
   padding: 0;
   text-align: left;
   &:hover { text-decoration: underline; }
`;

const LogoRemoveBtn = styled.button`
   background: none;
   border: none;
   color: ${colors.DANGER};
   font-size: 12px;
   cursor: pointer;
   padding: 0;
   text-align: left;
   &:hover { text-decoration: underline; }
`;

const DescriptionSection = styled.div`
   margin-top: 8px;
`;

const Form = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 1rem;
   /* margin-bottom: 1rem; */
   /* margin-top: 0; */

   @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 0;
   }
`;

const TemplateSection = styled.div`
   margin-top: 1rem;
`;

const TemplateGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   gap: 10px;
   margin-top: 6px;
   @media (max-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
   }
`;

const TemplateCard = styled.div<{ $active: boolean }>`
   border: 2px solid ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY12)};
   border-radius: 10px;
   padding: 8px;
   cursor: pointer;
   transition: all 0.15s;
   background: ${({ $active }) => ($active ? `${colors.PRIMARY}08` : 'transparent')};
   &:hover {
      border-color: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY10)};
   }
`;

const TemplatePreview = styled.div<{ $gradient: string }>`
   background: ${({ $gradient }) => $gradient};
   border-radius: 6px;
   height: 38px;
   display: flex;
   align-items: center;
   justify-content: center;
   gap: 6px;
   padding: 0 10px;
`;

const TemplatePreviewLabel = styled.span<{ $color: string }>`
   font-size: 11px;
   font-weight: 700;
   color: ${({ $color }) => $color};
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
`;

const TemplateName = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   text-align: center;
   margin-top: 5px;
   font-weight: 500;
`;
const BtnLabel = styled.p`
   padding-bottom: 5px;
   color: ${colors.GRAY5};
   font-size: 15px;
`;

const ActiveCheck = styled.div`
   position: absolute;
   right: -10px;
   top: -10px;
   background-color: var(--app-surface);
   border-radius: 50px;
   /* padding: 0; */
`;

const IntegrationContent = styled.div`
   margin-top: 1rem;
   padding-bottom: 1rem;

   /* text-align: center; */
`;
const CodeEditorWrapper = styled.div`
   border: 1px solid ${colors.GRAY12};
   border-radius: 4px;
   margin-top: 2px;
`;
const CodeEditorHead = styled.div`
   background-color: ${colors.GRAY4};
   text-align: right;
   display: flex;
   align-items: center;
   justify-content: space-between;
   border-top-left-radius: 4px;
   border-top-right-radius: 4px;
   /* display: grid;
   align-items: center; */
`;
const CodeEditorHeadTitle = styled.p`
   /* padding: 1px 10px; */
   /* border-bottom: 1px solid ${colors.GRAY12}; */
   font-size: 12px;
   text-align: right;
   color: white;
   display: flex;
   border-radius: 2px;
   margin-right: 10px;
   padding: 2px 5px;
   border-radius: 4px;
`;
const CodeEditorHeadLabel = styled.div`
   /* padding: 1px 10px; */
   /* border-bottom: 1px solid ${colors.GRAY12}; */
   font-size: 12px;
   text-align: right;
   color: white;
   display: flex;
   border-radius: 2px;
   margin-right: 10px;
   padding: 2px 5px;
   border-radius: 4px;
   cursor: pointer;

   &:hover {
      color: ${colors.GRAY13};
      background-color: ${colors.GRAY2};
   }
`;
const CodeEditorBody = styled.div`
   font-size: 14px;

   pre {
      margin: 0 !important;
   }
   code {
      background: ${colors.GRAY14} !important;
      white-space: pre-wrap !important; /* Allows text to wrap instead of horizontal scrolling */
      word-break: break-word !important; /* Ensures long words break properly */
      max-height: 150px !important; /* Set a max height to enable vertical scrolling */
      overflow-y: auto !important; /* Enable vertical scrolling */
      overflow-x: hidden !important; /* Hide horizontal scrolling */
      border-bottom-left-radius: 4px;
      border-bottom-right-radius: 4px;
      color: ${colors.PRIMARY} !important;

      /* width */
      &::-webkit-scrollbar {
         width: 7px;
         height: 7px;
      }

      /* Track */
      &::-webkit-scrollbar-track {
         // box-shadow: inset 0 0 5px grey;
         border-radius: 10px;
      }

      /* Handle */
      &::-webkit-scrollbar-thumb {
         background: ${colors.GRAY11};
         border-radius: 10px;
      }

      /* Handle on hover */
      &::-webkit-scrollbar-thumb:hover {
         background: ${colors.GRAY10};
      }
   }
`;

const ActionWrapper = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 10px;
   /* margin-top: 1rem; */
`;

const Actions = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 10px;
   margin-top: 1rem;
`;
export default UpdateStore;
